import React from 'react';
import { toast } from 'react-toastify';
import { setAuthProfile } from '../../../../../../../actions/auth';
import { validatePasswordStrength } from '../../../../../../../helpers/password-helper';
import { toAbsoluteApiUrl } from '../../../../../../../helpers/http-client';
import { uploadFileAsset } from '../../../../../../../helpers/file-helper';
import { getMyProfile, updateMyPassword, updateMyProfile } from '../../../../../../../helpers/user-helper';
import { getPasswordRules, resolveFilePublicUrl } from '../helpers/myProfileConfig';

export const useMyProfile = ({
    id,
    token,
    name,
    initialPhotoUrl,
    initialAvailable,
    dispatch,
}) => {
    const [isLoading, setIsLoading] = React.useState(true);
    const [isSavingPhone, setIsSavingPhone] = React.useState(false);
    const [isSavingSignature, setIsSavingSignature] = React.useState(false);
    const [isSavingAvatar, setIsSavingAvatar] = React.useState(false);
    const [isChangingPassword, setIsChangingPassword] = React.useState(false);

    const [phone, setPhone] = React.useState('');
    const [isAvailable, setIsAvailable] = React.useState(Boolean(initialAvailable));
    const [signatureHtml, setSignatureHtml] = React.useState('');
    const [avatarUrl, setAvatarUrl] = React.useState(String(initialPhotoUrl || ''));

    const [currentPassword, setCurrentPassword] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');

    const avatarInputRef = React.useRef(null);
    const signatureEditorRef = React.useRef(null);

    const passwordStrength = React.useMemo(() => {
        return validatePasswordStrength(String(newPassword || ''));
    }, [newPassword]);

    const passwordRules = React.useMemo(() => {
        return getPasswordRules(passwordStrength);
    }, [passwordStrength]);

    const applyProfilePatch = React.useCallback(async (payload, options = {}) => {
        if (!token) {
            return false;
        }

        const resp = await updateMyProfile(token, payload);
        if (!resp?.success || !resp?.data) {
            if (options.onErrorMessage) {
                toast.error(options.onErrorMessage);
            } else {
                toast.error(resp?.message || 'No se pudo actualizar el perfil.');
            }
            return false;
        }

        const user = resp.data;
        const resolvedPhotoUrl = toAbsoluteApiUrl(user.photoUrl || '');

        dispatch(setAuthProfile({
            available: Boolean(user.available),
            photoUrl: resolvedPhotoUrl,
            phone: String(user.phone || ''),
            ticketSignature: String(user.ticketSignature || ''),
            active: Boolean(user.active),
        }));

        setIsAvailable(Boolean(user.available));
        setAvatarUrl(resolvedPhotoUrl);
        setPhone(String(user.phone || ''));
        setSignatureHtml(String(user.ticketSignature || ''));

        if (options.successMessage) {
            toast.success(options.successMessage);
        }

        return true;
    }, [dispatch, token]);

    React.useEffect(() => {
        const loadProfile = async () => {
            if (!token || !id) {
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            const resp = await getMyProfile(token, id);

            if (!resp?.success || !resp?.data) {
                toast.error(resp?.message || 'No se pudo cargar el perfil.');
                setIsLoading(false);
                return;
            }

            const user = resp.data;
            const resolvedPhotoUrl = toAbsoluteApiUrl(user.photoUrl || '');

            setPhone(String(user.phone || ''));
            setIsAvailable(Boolean(user.available));
            setSignatureHtml(String(user.ticketSignature || ''));
            setAvatarUrl(resolvedPhotoUrl);

            dispatch(setAuthProfile({
                available: Boolean(user.available),
                photoUrl: resolvedPhotoUrl,
                phone: String(user.phone || ''),
                ticketSignature: String(user.ticketSignature || ''),
                active: Boolean(user.active),
            }));

            setIsLoading(false);
        };

        loadProfile();
    }, [dispatch, id, token]);

    React.useEffect(() => {
        if (!signatureEditorRef.current) {
            return;
        }

        if (signatureEditorRef.current.innerHTML !== String(signatureHtml || '')) {
            signatureEditorRef.current.innerHTML = signatureHtml || '';
        }
    }, [signatureHtml]);

    const handleAvailabilityChange = async (event) => {
        const nextValue = Boolean(event.target.checked);
        setIsAvailable(nextValue);

        const success = await applyProfilePatch({ available: nextValue }, {
            onErrorMessage: 'No se pudo actualizar la disponibilidad.',
            successMessage: 'Disponibilidad actualizada.',
        });

        if (!success) {
            setIsAvailable((previous) => !previous);
        }
    };

    const handlePhoneSave = async () => {
        if (isSavingPhone) {
            return;
        }

        setIsSavingPhone(true);
        await applyProfilePatch({ phone: String(phone || '').trim() }, {
            successMessage: 'Teléfono actualizado.',
        });
        setIsSavingPhone(false);
    };

    const handleSignatureSave = async () => {
        if (isSavingSignature) {
            return;
        }

        setIsSavingSignature(true);
        await applyProfilePatch({ ticketSignature: String(signatureHtml || '').trim() }, {
            successMessage: 'Firma guardada.',
        });
        setIsSavingSignature(false);
    };

    const handleAvatarSelected = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !token) {
            return;
        }

        setIsSavingAvatar(true);

        const uploadResp = await uploadFileAsset(token, {
            file,
            purpose: 'PROFILE_AVATAR',
            tags: ['profile', 'avatar'],
            referenceType: 'USER',
            referenceId: String(id || ''),
            referenceField: 'photoUrl',
            referenceLabel: String(name || 'Usuario'),
            isPublic: true,
        });

        if (!uploadResp?.success || !uploadResp?.data) {
            toast.error(uploadResp?.message || 'No se pudo subir la imagen de perfil.');
            setIsSavingAvatar(false);
            event.target.value = '';
            return;
        }

        const nextPhotoUrl = resolveFilePublicUrl(uploadResp.data || {});

        if (!nextPhotoUrl) {
            toast.error('No se pudo obtener la URL de la imagen subida.');
            setIsSavingAvatar(false);
            event.target.value = '';
            return;
        }

        const patched = await applyProfilePatch({ photoUrl: nextPhotoUrl }, {
            successMessage: 'Imagen de perfil actualizada.',
        });

        if (patched) {
            setAvatarUrl(nextPhotoUrl);
            dispatch(setAuthProfile({ photoUrl: nextPhotoUrl }));
        }

        setIsSavingAvatar(false);
        event.target.value = '';
    };

    const handleChangePassword = async (event) => {
        event.preventDefault();

        if (!token || isChangingPassword) {
            return;
        }

        if (!currentPassword || !newPassword || !confirmPassword) {
            toast.error('Completa todos los campos de contraseña.');
            return;
        }

        if (!passwordStrength.isValid) {
            toast.error('La nueva contraseña no cumple los requisitos mínimos de seguridad.');
            return;
        }

        if (newPassword !== confirmPassword) {
            toast.error('La nueva contraseña no coincide con la confirmación.');
            return;
        }

        if (currentPassword === newPassword) {
            toast.error('La nueva contraseña debe ser diferente a la actual.');
            return;
        }

        setIsChangingPassword(true);

        const resp = await updateMyPassword(token, {
            currentPassword,
            newPassword,
            confirmPassword,
        });

        if (!resp?.success) {
            toast.error(resp?.message || 'No se pudo actualizar la contraseña.');
            setIsChangingPassword(false);
            return;
        }

        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        toast.success(resp?.message || 'Contraseña actualizada correctamente.');
        setIsChangingPassword(false);
    };

    const runEditorCommand = (command) => {
        if (signatureEditorRef.current) {
            signatureEditorRef.current.focus();
        }

        document.execCommand(command, false);
        const nextHtml = signatureEditorRef.current?.innerHTML || '';
        setSignatureHtml(nextHtml);
    };

    return {
        isLoading,
        isSavingPhone,
        isSavingSignature,
        isSavingAvatar,
        isChangingPassword,
        phone,
        isAvailable,
        signatureHtml,
        avatarUrl,
        currentPassword,
        newPassword,
        confirmPassword,
        avatarInputRef,
        signatureEditorRef,
        passwordRules,
        setPhone,
        setSignatureHtml,
        setCurrentPassword,
        setNewPassword,
        setConfirmPassword,
        handleAvailabilityChange,
        handlePhoneSave,
        handleSignatureSave,
        handleAvatarSelected,
        handleChangePassword,
        runEditorCommand,
    };
};
